import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { SectionHeading, UnifiedFooter } from '../components';
import { Nav } from '../components/Nav';

const API_URL = 'https://syntra--deepfake-detector-api-wrapper.modal.run/classify_audio';

const PlaygroundPage: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<null | number>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setResult(null);
    setError(null);
    const f = e.target.files?.[0];
    if (!f) return;
    const validTypes = ['audio/wav', 'audio/wave', 'audio/x-wav', 'audio/mp3', 'audio/mpeg'];
    if (!validTypes.includes(f.type)) {
      setError('Please upload a .wav or .mp3 file.');
      setFile(null);
      return;
    }
    setFile(f);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    if (!file) {
      setError('No file selected.');
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(API_URL, {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) throw new Error('Server error');
      const data = await response.json();
      if (typeof data.predicted_class !== 'number') throw new Error('Invalid response');
      setResult(data.predicted_class);
    } catch (err: any) {
      setError(err.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: 'beforeChildren',
        staggerChildren: 0.1,
      },
    },
  };
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  return (
    <motion.div
      className="min-h-screen bg-white text-dark-900 font-sans overflow-x-clip"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <Nav activeLink="deepfake" />
      <motion.section className="pt-40 pb-24 bg-white" variants={itemVariants}>
        <div className="page-padding">
          <div className="container-medium">
            <div className="max-w-2xl mx-auto">
              <SectionHeading
                caption="DEEPFAKE DETECTOR"
                title={<span className="leading-tight">Deepfake Audio Detector</span>}
                description={
                  <>
                    Upload a .wav or .mp3 audio file. We'll analyze it and tell you if it's a deepfake.
                  </>
                }
                className="text-left mb-10"
              />
              <motion.div
                className="bg-white rounded-2xl shadow-lg border border-blue-100 p-8 flex flex-col items-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <form onSubmit={handleSubmit} className="w-full flex flex-col items-center gap-6">
                  <input
                    type="file"
                    accept=".wav,.mp3,audio/wav,audio/mp3,audio/mpeg"
                    onChange={handleFileChange}
                    className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  {error && <div className="text-red-500 text-sm w-full text-center">{error}</div>}
                  <button
                    type="submit"
                    className="bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-all duration-300 text-center w-full disabled:opacity-50"
                    disabled={!file || loading}
                  >
                    {loading ? 'Analyzing...' : 'Analyze Audio'}
                  </button>
                </form>
                {loading && (
                  <div className="mt-8 flex items-center gap-2">
                    <svg className="animate-spin h-6 w-6 text-blue-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                    </svg>
                    <span>Processing...</span>
                  </div>
                )}
                {result !== null && !loading && (
                  <div className="mt-8 text-lg font-semibold w-full text-center">
                    Result: {result === 0 ? (
                      <span className="text-red-600">Deepfake Detected</span>
                    ) : (
                      <span className="text-green-600">Not a Deepfake</span>
                    )}
                  </div>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </motion.section>
      <UnifiedFooter />
    </motion.div>
  );
};

export default PlaygroundPage; 